import React from 'react';
import { MdCloudOff } from 'react-icons/md';
import {
  BUTTON_SIZE_MD,
  BUTTON_VARIANT_FILLED,
  BUTTON_VARIANT_TEXT,
  Button,
  MODAL_SIZE_MD,
  Modal,
} from '@aibuild/ui';
import { Body, FooterRow } from './DriftBlockerModal.styled';

export interface DriftBlockerModalProps {
  /** Whether the modal is currently rendered (auto-save is in `drift`). */
  open: boolean;
  /**
   * Pull the latest server state into the editor and discard the
   * user's local edits. Wired by the page to invalidate the detail
   * query — the auto-save loop re-baselines off the fresh payload.
   */
  onRefresh: () => void;
  /**
   * Last-writer-wins overwrite: keep the user's local edits and force
   * the PUT through with the server's current `updatedAt`. Provided
   * by `useProcessAutoSave.forceSave`.
   */
  onForceSave: () => void;
  /**
   * True while the force-save PUT is in flight. Disables both buttons
   * and surfaces a spinner on the force-save action so the user can't
   * fire a second mutation while we're awaiting the first.
   */
  isForceSaving?: boolean;
  dataTestId?: string;
}

/**
 * Blocking modal shown when `PUT /processes/:id` returned 409
 * `DRIFT_DETECTED`. Replaces the earlier dismissible {@code DriftWarning}
 * banner (AIB-6976), which silently lost edits made while the snackbar
 * was visible because the auto-save loop is suspended in `drift`.
 *
 * Two escape hatches — both confirmation-level actions:
 *   - **Discard my edits & reload** — calls {@link onRefresh}; the
 *     page invalidates the detail query so the editor reloads from
 *     the server and the user's local edits are lost.
 *   - **Keep my edits & overwrite** — calls {@link onForceSave};
 *     {@link useProcessAutoSave.forceSave} re-PUTs with the server's
 *     current `updatedAt`, last-writer-wins.
 *
 * The page also flips the editor to read-only while this modal is
 * open ({@code effectiveReadOnly}) so the canvas can't accept further
 * edits behind the backdrop. Together those two guards close the
 * silent-edit-loss window described in AIB-6976.
 *
 * Dismissal is intentionally disabled: Escape, backdrop click, and the
 * close button all no-op. The user MUST commit to one of the two
 * actions — there's no "Maybe later" path because every additional
 * edit made during drift is at risk.
 */
const DriftBlockerModal: React.FC<DriftBlockerModalProps> = ({
  open,
  onRefresh,
  onForceSave,
  isForceSaving = false,
  dataTestId = 'drift-blocker-modal',
}) => {
  // `noop` for `onClose` -- the modal's own dismiss paths are all
  // disabled via the props below, so this is only here to satisfy the
  // shared Modal contract. We never actually want this called.
  const noop = React.useCallback(() => {}, []);

  return (
    <Modal
      open={open}
      onClose={noop}
      title="Process changed elsewhere"
      icon={<MdCloudOff size={24} aria-hidden />}
      size={MODAL_SIZE_MD}
      hideCloseButton
      disableEscape
      disableBackdropClose
      dataTestId={dataTestId}
      footer={
        <FooterRow data-testid={`${dataTestId}__footer-row`}>
          <Button
            variant={BUTTON_VARIANT_TEXT}
            size={BUTTON_SIZE_MD}
            onClick={onRefresh}
            disabled={isForceSaving}
            dataTestId={`${dataTestId}__refresh`}
          >
            Discard my edits &amp; reload
          </Button>
          <Button
            variant={BUTTON_VARIANT_FILLED}
            size={BUTTON_SIZE_MD}
            onClick={onForceSave}
            loading={isForceSaving}
            dataTestId={`${dataTestId}__force-save`}
          >
            Keep my edits &amp; overwrite
          </Button>
        </FooterRow>
      }
    >
      <Body data-testid={`${dataTestId}__copy`}>
        You saved changes to this process in another tab while you were
        editing here. How would you like to proceed?
      </Body>
    </Modal>
  );
};

export default DriftBlockerModal;
